<template>
  <main class="table">
    <!-- Async room: who the game is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <section class="status">
      <span class="status__round">{{ roundLabel }}</span>
      <span class="status__money" title="Your money - it counts at face value at the end">
        <i class="coin" aria-hidden="true" />{{ money(me?.coins ?? 0) }}
      </span>
    </section>

    <section class="market-region">
      <MarketRow v-if="!game.meta.roundOver" />

      <!-- Between rounds: what just happened, laid out card by card. -->
      <div v-else class="summary">
        <template v-if="game.meta.roundOver.kind === 'auction'">
          <p class="verdict">{{ auctionVerdict }}</p>
        </template>
        <template v-else>
          <ol class="reveal">
            <li v-for="row in game.meta.roundOver.results" :key="row.seat" class="reveal__row">
              <span class="reveal__name" :class="{ 'reveal__name--me': row.seat === me?.seat }">
                {{ row.name }}
              </span>
              <CardFace :card-id="row.property" />
              <span class="reveal__arrow" aria-hidden="true">→</span>
              <CardFace :card-id="row.cheque" />
            </li>
          </ol>
        </template>
      </div>
    </section>

    <section class="seats-region">
      <SeatList />
    </section>

    <section class="actions">
      <!-- Between rounds: the way on. -->
      <template v-if="game.meta.roundOver">
        <button class="primary" @click="$store.dispatch('nextRound')">
          {{ nextLabel }}
        </button>
      </template>

      <!-- Buying phase, my turn: raise or pass. -->
      <template v-else-if="myTurnToBid">
        <div v-if="myBidRange.canBid" class="bid-controls">
          <button
            class="bid-step" aria-label="Bid less"
            :disabled="bidAmount <= myBidRange.min"
            @click="bidAmount -= 1"
          >−</button>
          <button class="go bid-go" @click="commitBid">
            <strong>Bid {{ money(bidAmount) }}</strong>
            <small>{{ bidHint }}</small>
          </button>
          <button
            class="bid-step" aria-label="Bid more"
            :disabled="bidAmount >= myBidRange.max"
            @click="bidAmount += 1"
          >+</button>
        </div>
        <button class="leave" @click="$store.dispatch('passMyBid')">
          <strong>Pass — take house {{ lowestValue }}</strong>
          <small>{{ passHint }}</small>
        </button>
      </template>

      <!-- Selling phase: my hand, tap to pick. -->
      <template v-else-if="isSelling && me && me.properties.length">
        <p class="hand-prompt">{{ handPrompt }}</p>
        <div class="hand">
          <button
            v-for="id in myHand"
            :key="id"
            class="hand__card"
            :aria-pressed="me.pick === id"
            @click="$store.dispatch('pickMyHouse', id)"
          >
            <CardFace :card-id="id" :selected="me.pick === id" :dimmed="me.pick !== null && me.pick !== id" />
          </button>
        </div>
      </template>

      <!-- Not my turn, or watching. -->
      <p v-else class="waiting">{{ waitingText }}</p>

      <!-- The buying phase must still show you what you own - "check your
           hand" is meaningless if the hand only exists in a hover title.
           Read-only here; it becomes tappable in the selling phase above. -->
      <div v-if="isBuying && me && me.properties.length" class="owned">
        <span class="owned__label">your houses</span>
        <div class="owned__cards">
          <CardFace v-for="id in myHand" :key="id" :card-id="id" />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '@/store/asyncPlay.js';
import MarketRow from '@/components/game/MarketRow.vue';
import SeatList from '@/components/game/SeatList.vue';
import CardFace from '@/components/game/CardFace.vue';
import { cardById } from '@/engine/deck';
import { PHASES, passCost } from '@/engine/constants';

// The table. Four regions - status, market, seats, actions - placed by
// grid-template-areas so the same markup stacks on a phone and splits in two
// on a tablet. The action bar is sized `auto` in the grid so it always gets
// its height first and can never be pushed off the bottom - the trap
// No Thanks! hit and documented.
export default {
  name: 'GameTable',
  components: { MarketRow, SeatList, CardFace },
  data() {
    return { bidAmount: 1 };
  },
  computed: {
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.$store.state.user?.uid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      if (!meta?.async || !this.game) return '';
      const nameOf = (id) => this.game.players.find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.$store.state.user?.uid, nameOf);
    },
    ...mapState(['game']),
    ...mapGetters([
      'me', 'myTurnToBid', 'awaitingMyPick', 'myBidRange', 'isBuying', 'isSelling',
      'tableHighBid', 'lowestHouseId', 'picksRevealed', 'isOnline',
    ]),

    roundLabel() {
      const total = this.roundsInPhase;
      if (this.game.meta.phase === PHASES.BUYING) return `Auction ${this.game.meta.round} of ${total}`;
      if (this.game.meta.phase === PHASES.SELLING) return `Sale ${this.game.meta.round} of ${total}`;
      return 'Counting up';
    },

    /** Rounds this phase will have: dealt so far + still in the deck. */
    roundsInPhase() {
      const count = this.game.players.length;
      const deck = this.game.meta.phase === PHASES.BUYING
        ? this.game.propertyDeck.length
        : this.game.chequeDeck.length;
      return this.game.meta.round + Math.ceil(deck / count);
    },

    lowestValue() {
      return this.lowestHouseId ? cardById(this.lowestHouseId).value : '';
    },

    bidHint() {
      return this.tableHighBid > 0
        ? `the table is at ${this.money(this.tableHighBid)}`
        : 'open the bidding';
    },

    passHint() {
      const cost = passCost(this.me?.bid || 0);
      return cost > 0 ? `half your bid, ${this.money(cost)}, is spent` : 'for free';
    },

    auctionVerdict() {
      const over = this.game.meta.roundOver;
      const winner = this.game.players[over.winnerSeat];
      const house = winner ? cardById(winner.properties[winner.properties.length - 1]).value : '';
      const mine = winner?.id === this.me?.id;
      return `${mine ? 'You' : winner?.name} won house ${house}. Everyone got something - check your hand.`;
    },

    nextLabel() {
      const over = this.game.meta.roundOver;
      if (over.kind === 'auction' && over.nextPhase === PHASES.SELLING) return 'Start selling';
      if (over.kind === 'sale' && over.finished) return 'Count the money';
      return over.kind === 'auction' ? `Auction ${this.game.meta.round + 1}` : `Sale ${this.game.meta.round + 1}`;
    },

    myHand() {
      return [...(this.me?.properties || [])].sort(
        (a, b) => cardById(b).value - cardById(a).value,
      );
    },

    handPrompt() {
      if (this.me.pick === null) return 'Pick a house to sell — best house takes the best cheque.';
      if (!this.picksRevealed) return 'Locked in. You can change it until everyone is ready.';
      return 'Turning them over…';
    },

    waitingText() {
      if (!this.me) return 'Watching.';
      if (this.game.meta.phase === PHASES.BUYING) {
        if (this.me.passed) return `You're out this round with house ${this.myNewestHouse}.`;
        const holder = this.game.players[this.game.meta.turnSeat];
        return holder ? `Waiting for ${holder.name}…` : '';
      }
      return 'Turning them over…';
    },

    myNewestHouse() {
      const latest = this.me?.properties[this.me.properties.length - 1];
      return latest ? cardById(latest).value : '';
    },
  },
  watch: {
    /**
     * A fresh turn re-baselines the stepper to the minimum legal raise.
     * Watching the range rather than the turn means an opponent's re-raise
     * while I dither also snaps me back to legal.
     */
    myBidRange: {
      immediate: true,
      handler(range) {
        if (range?.canBid) {
          this.bidAmount = Math.min(Math.max(this.bidAmount, range.min), range.max);
          if (this.bidAmount < range.min) this.bidAmount = range.min;
        }
      },
    },
    myTurnToBid(mine) {
      if (mine && this.myBidRange.canBid) this.bidAmount = this.myBidRange.min;
    },
  },
  methods: {
    money(thousands) {
      return `$${(thousands * 1000).toLocaleString('en-US')}`;
    },
    commitBid() {
      this.$store.dispatch('placeMyBid', this.bidAmount);
    },
  },
};
</script>

<style scoped>
.waiting-banner { margin: 0; padding: 0.5rem 0.9rem; border-radius: 0.6rem; background: rgba(0,0,0,0.08); font-size: 0.95rem; }
.waiting-banner.is-mine { background: rgba(0,0,0,0.16); font-weight: 700; }
</style>